import {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import { WobaLogger, HttpLogger } from "@woba/logger-web";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    _loggerCtx?: { requestId: string; startTime: number };
  }
}

export const createLoggerInterceptor = (
  axiosInstance: AxiosInstance,
  logger: WobaLogger,
): void => {
  const httpLogger = new HttpLogger(logger);

  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const ctx = httpLogger.onRequest({
        method: config.method || "GET",
        url: config.url || "",
        baseURL: config.baseURL,
      });

      Object.entries(ctx.headers).forEach(([key, value]) => {
        config.headers[key] = value;
      });

      config._loggerCtx = {
        requestId: ctx.requestId,
        startTime: ctx.startTime,
      };

      return config;
    },
    (error: AxiosError) => {
      logger.error("HTTP Request Error", error, { type: "http_request_error" });
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const ctx = response.config._loggerCtx;

      httpLogger.onResponse(
        {
          method: response.config.method || "GET",
          url: response.config.url || "",
          baseURL: response.config.baseURL,
          statusCode: response.status,
          durationMs: ctx ? Date.now() - ctx.startTime : 0,
        },
        ctx?.requestId,
      );

      return response;
    },
    (error: AxiosError) => {
      const config = error.config;
      const ctx = config?._loggerCtx;

      httpLogger.onError(
        {
          method: config?.method || "GET",
          url: config?.url || "",
          baseURL: config?.baseURL,
          statusCode: error.response?.status,
          durationMs: ctx ? Date.now() - ctx.startTime : 0,
          errorMessage: error.message,
        },
        error,
        ctx?.requestId,
      );

      return Promise.reject(error);
    },
  );
};
